<template>
  <div class="reader-immersive min-h-screen text-ink-900">
    <button class="reader-back-btn" aria-label="返回" @click="goBackToBookshelf">‹</button>

    <div class="min-h-screen">
      <!-- 加载状态 -->
      <div v-if="isLoading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-400"></div>
        <span class="ml-3 text-gray-600">加载中...</span>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <div class="text-red-500 text-4xl mb-4">⚠️</div>
        <h3 class="text-lg font-medium text-red-800 mb-2">加载失败</h3>
        <p class="text-red-700 mb-4">{{ error }}</p>
        <button @click="retryLoading" class="px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600">
          重试
        </button>
      </div>

      <!-- 主阅读区域 -->
      <div v-else class="reader-shell">
        <div class="reader-canvas">
          <!-- 章节内容（沉浸阅读区） -->
          <div
            class="reader-page relative p-5 md:p-10"
            :class="[readerPaperClass, readingModeClass]"
            :style="readerBrightnessStyle"
            @click="handleReaderTap"
            @touchstart.passive="handleTouchStart"
            @touchend.passive="handleTouchEnd"
          >
            <button v-if="readingMode === 'page' && showChapterText" class="reader-tap-zone left-0" aria-label="上一页" @click="previousPage"></button>
            <button v-if="readingMode === 'page' && showChapterText" class="reader-tap-zone right-0" aria-label="下一页" @click="nextPage"></button>
            <button
              v-if="showDesktopPageControls"
              class="reader-desktop-page-btn reader-desktop-page-prev"
              aria-label="上一页"
              @click.stop="previousPage"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 4 7 12l8 8" />
              </svg>
            </button>
            <button
              v-if="showDesktopPageControls"
              class="reader-desktop-page-btn reader-desktop-page-next"
              aria-label="下一页"
              @click.stop="nextPage"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m9 4 8 8-8 8" />
              </svg>
            </button>

            <div v-if="showReaderWatermark" class="reader-watermark" aria-hidden="true">
              <div class="reader-watermark-brand">菠萝蜜·沉浸式AI小说</div>
              <div class="reader-watermark-title">{{ book.title }}</div>
            </div>

            <!-- 访问权限提示 -->
            <div v-if="!chapter.hasAccess" class="mb-6 rounded-xl border border-jackfruit-200 bg-jackfruit-50 p-4">
              <div class="flex items-start">
                <div class="mr-3 text-xl text-jackfruit-500">🔒</div>
                <div>
                  <h3 class="mb-1 font-black text-ink-900">本章需要蜜豆解锁</h3>
                  <p class="mb-3 text-sm text-ink-600">第 {{ unlockPack.start }}-{{ unlockPack.end }} 章共 {{ unlockPack.price }} 蜜豆，解锁后永久可读。</p>
                  <button class="rounded-lg bg-jackfruit-500 px-4 py-2 text-sm font-black text-ink-900 hover:bg-jackfruit-600" @click="unlockChapter">
                    {{ isUnlocking ? '解锁中...' : '立即解锁' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- 章节内容 -->
            <div class="reader-prose max-w-none text-ink-800" :style="readerStyle">
              <!-- 付费章节未购买提示 -->
              <div v-if="chapter.hasAccess === false && chapter.accessReason === 'need_payment'" class="mb-8 rounded-2xl border border-jackfruit-200 bg-jackfruit-50 p-8 text-center">
                <div class="mb-4 text-5xl text-jackfruit-500">🔒</div>
                <h3 class="mb-2 text-xl font-black text-ink-900">付费章节包</h3>
                <p class="mb-4 text-ink-700">
                  第 {{ unlockPack.start }}-{{ unlockPack.end }} 章需要 <span class="font-black text-pulp-600">{{ unlockPack.price }} 蜜豆</span>
                </p>
                <p class="mb-6 text-sm text-ink-500">
                  当前余额 {{ honeyBeanBalance }} 蜜豆，购买后永久有效。
                </p>
                <button 
                  @click="unlockChapter"
                  :disabled="isUnlocking"
                  class="rounded-xl bg-jackfruit-500 px-6 py-3 font-black text-ink-900 shadow-fruit transition hover:bg-jackfruit-600 disabled:opacity-50"
                >
                  <span v-if="isUnlocking" class="animate-spin mr-2">⟳</span>
                  {{ isUnlocking ? '解锁中...' : '用蜜豆解锁' }}
                </button>
                <p class="mt-4 text-xs text-ink-400">
                  模拟支付环境
                </p>
              </div>
              
              <!-- 章节正文 -->
              <div v-else-if="showChapterText" class="reader-text text-ink-800">
                <template v-for="(paragraph, index) in displayedParagraphs" :key="`${currentChapter}-${currentPageIndex}-${index}`">
                  <h1 v-if="paragraph.isTitle" class="reader-chapter-title">{{ paragraph.text }}</h1>
                  <p v-else class="reader-paragraph">{{ paragraph.text }}</p>
                </template>
              </div>
              
              <div v-if="showChapterEnding" class="reader-ending-panel">
                <div class="reader-ending-header text-center">
                  <div class="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-jackfruit-100 text-2xl">🍃</div>
                  <h3 class="text-xl font-black text-ink-900">{{ chapterEndTitle }}</h3>
                  <p class="mt-2 text-sm text-ink-500">{{ chapterEndDescription }}</p>
                  <div v-if="showBranchComposer" class="flex items-center justify-center mt-2 gap-1.5">
                    <div class="w-2 h-2 rounded-full" :class="{
                      'bg-green-400': aiStatusColor === 'green',
                      'bg-red-400': aiStatusColor === 'red',
                      'bg-gray-300': aiStatusColor === 'gray'
                    }"></div>
                    <span class="text-xs text-ink-500">{{ aiStatusText }}</span>
                  </div>
                </div>

                <div
                  v-if="showPublicCreatorContinue"
                  class="mx-auto mb-6 max-w-2xl rounded-2xl border border-jackfruit-100 bg-jackfruit-50/70 p-4"
                >
                  <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p class="text-sm font-black text-ink-900">继续阅读创作者已发布内容</p>
                      <p class="mt-1 text-xs leading-5 text-ink-500">
                        不改写剧情时，后续章节会按这本公共作品继续推进。
                      </p>
                    </div>
                    <button
                      v-if="currentChapter < book.totalChapters"
                      class="rounded-xl bg-jackfruit-500 px-5 py-2.5 text-sm font-black text-ink-900 shadow-fruit transition hover:bg-jackfruit-600"
                      @click="nextChapter"
                    >
                      下一章
                    </button>
                    <span v-else class="rounded-xl bg-white px-4 py-2 text-sm font-black text-ink-500">
                      已读到最新章
                    </span>
                  </div>
                  <div class="mt-4 flex flex-wrap justify-center gap-2">
                    <button
                      v-if="currentChapter >= book.totalChapters"
                      class="rounded-lg bg-jackfruit-500 px-3 py-2 text-xs font-black text-ink-900 transition hover:bg-jackfruit-600"
                      @click="openRewriteComposer"
                    >
                      AI续写
                    </button>
                    <button
                      class="rounded-lg border border-ink-100 bg-white px-3 py-2 text-xs font-black text-ink-500 transition hover:border-jackfruit-200 hover:bg-white"
                      @click="openRewriteComposer"
                    >
                      改写剧情方向
                    </button>
                  </div>
                </div>
                
                <template v-if="showBranchComposer">
                  <!-- 分支选项：读到文末直接展示 -->
                  <div v-if="branchOptions.length > 0" class="reader-branch-list mx-auto mb-6 grid max-w-2xl gap-2">
                    <div
                      v-for="(option, index) in branchOptions"
                      :key="index"
                      class="branch-choice"
                      :class="selectedBranchIndex === index ? 'border-jackfruit-400 bg-jackfruit-50' : 'border-jackfruit-100'"
                      @click.stop="confirmBranch(index)"
                    >
                      {{ option.summary || option.title }}
                    </div>

                    <div class="rounded-2xl border border-dashed border-jackfruit-200 bg-white/70 p-3">
                      <label class="text-xs font-black text-ink-700" for="custom-branch">自己写一个方向</label>
                      <textarea
                        id="custom-branch"
                        v-model="customBranchText"
                        rows="2"
                        maxlength="160"
                        class="mt-2 w-full resize-none rounded-xl border border-jackfruit-200 bg-white px-3 py-2 text-sm leading-6 text-ink-800 outline-none transition focus:border-jackfruit-400"
                        placeholder="比如：主角不接受系统任务，转而揭开医院背后的秘密。"
                      ></textarea>
                      <div class="mt-2 flex items-center justify-between gap-3">
                        <span class="text-xs text-ink-400">{{ customBranchText.length }}/160</span>
                        <button
                          @click="confirmCustomBranch"
                          :disabled="customBranchText.trim().length < 6 || !isAIAvailable"
                          class="rounded-xl bg-ink-900 px-4 py-2 text-sm font-black text-white transition hover:bg-ink-700 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          续写
                        </button>
                      </div>
                    </div>
                  </div>

                  <div v-if="canPublishAsCreator" class="mx-auto mb-6 max-w-2xl rounded-2xl border border-pulp-100 bg-pulp-50 p-4 text-center">
                    <div class="text-sm font-black text-pulp-700">创作者身份已解锁</div>
                    <p class="mt-1 text-xs leading-5 text-ink-500">
                      {{ creatorUnlockText }}
                    </p>
                    <button
                      class="mt-3 rounded-xl bg-jackfruit-500 px-5 py-2.5 text-sm font-black text-ink-900 shadow-fruit transition hover:bg-jackfruit-600"
                      @click="openPublishDialog"
                    >
                      以创作者身份保存并上架
                    </button>
                  </div>

                  <!-- 生成中 -->
                  <div v-else-if="isGeneratingBranch" class="text-center py-8">
                    <div class="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-jackfruit-500"></div>
                    <p class="text-sm text-ink-500">AI正在构思剧情走向...</p>
                  </div>

                  <!-- 确认中 -->
                  <div v-else-if="isConfirmingBranch" class="text-center py-8">
                    <div class="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-jackfruit-500"></div>
                    <p class="text-sm text-ink-500">正在为你续写下一章...</p>
                  </div>

                  <!-- AI不可用 -->
                  <div v-else-if="aiStatus && !aiStatus.deepseek.available && !isGeneratingBranch && !isConfirmingBranch" class="bg-amber-50/70 border border-amber-200 rounded-2xl p-6 text-center max-w-md mx-auto">
                    <div class="text-amber-400 text-3xl mb-2">🤖</div>
                    <p class="text-amber-700 text-sm">{{ aiStatus.deepseek.error || 'AI服务正在维护，请稍后再试' }}</p>
                  </div>
                </template>
              </div>
            </div>

            <div v-if="readingMode === 'page'" class="mt-8 hidden items-center justify-between border-t border-jackfruit-100 pt-5 md:flex">
              <button class="reader-page-btn" :disabled="!canGoPreviousPage" @click="previousPage">‹ 上一页</button>
              <span class="text-xs font-bold text-ink-400">{{ currentPageIndex + 1 }} / {{ pageCount }}</span>
              <button class="reader-page-btn" :disabled="!canGoNextPage" @click="nextPage">下一页 ›</button>
            </div>

            <div v-if="readingMode === 'page' && chapter.hasAccess !== false" class="reader-page-count">
              {{ globalPageIndex }} / {{ globalPageCount }}
            </div>

            <button
              v-if="showScrollNextChapterButton"
              class="reader-scroll-next-btn"
              @click.stop="nextChapter"
            >
              下一章
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="reader-bottom-bar" :class="{ 'is-visible': showReaderControls }">
      <button class="reader-bottom-btn" title="目录" @click="showToc = true" v-html="readerIcons.menu"></button>
      <button class="reader-bottom-btn" title="书签" @click="toggleBookmark" v-html="readerIcons.bookmark"></button>
      <button class="reader-bottom-btn reader-letter-btn" title="字体" @click="showReaderSettings = true">A</button>
      <button class="reader-bottom-btn" title="亮度" @click="showBrightnessPanel = true" v-html="readerIcons.brightness"></button>
      <button class="reader-bottom-btn" title="进度" @click="showProgressPanel = true" v-html="readerIcons.progress"></button>
      <button class="reader-bottom-btn" title="阅读模式" @click="toggleReadingMode" v-html="readingMode === 'page' ? readerIcons.pageMode : readerIcons.scrollMode"></button>
    </div>

    <div v-if="showReaderSettings" class="reader-panel-layer" @click.self="showReaderSettings = false">
      <div class="reader-settings-panel">
        <div class="space-y-5">
          <div>
            <div class="reader-slider-row">
              <span class="text-sm font-black text-ink-500">A</span>
              <input v-model.number="readerFontSize" class="accent-yellow-400 flex-1" type="range" min="15" max="24" />
              <span class="text-xl font-black text-ink-600">A</span>
            </div>
          </div>
          <div class="reader-segment">
            <button class="reader-segment-btn" @click="decreaseFontSize">小</button>
            <button class="reader-segment-btn" @click="increaseFontSize">大</button>
          </div>
          <div class="grid grid-cols-4 gap-2">
            <button
              v-for="font in readerFonts"
              :key="font.value"
              class="reader-font-btn"
              :class="{ active: readerFont === font.value }"
              @click="readerFont = font.value"
            >
              {{ font.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showBrightnessPanel" class="reader-panel-layer" @click.self="showBrightnessPanel = false">
      <div class="reader-compact-panel">
        <div class="reader-slider-row">
          <span class="text-lg">☾</span>
          <input v-model.number="readerBrightness" class="accent-yellow-400 flex-1" type="range" min="72" max="112" />
          <span class="text-lg">☼</span>
        </div>
        <div class="reader-paper-row">
          <button
            v-for="paper in paperOptions"
            :key="paper.value"
            class="reader-paper-swatch"
            :class="{ active: readerPaper === paper.value }"
            :style="{ background: paper.preview }"
            :title="paper.label"
            @click="readerPaper = paper.value"
          ></button>
        </div>
      </div>
    </div>

    <div v-if="showProgressPanel" class="reader-panel-layer" @click.self="showProgressPanel = false">
      <div class="reader-compact-panel">
        <div class="h-2 overflow-hidden rounded-full bg-jackfruit-100">
          <div class="h-full rounded-full bg-jackfruit-pop" :style="{ width: readingProgressPercent + '%' }"></div>
        </div>
        <p class="mt-3 text-center text-xs font-bold text-ink-500">{{ currentChapter }}/{{ book.totalChapters }} 章 · {{ currentPageIndex + 1 }}/{{ pageCount }} 页 · {{ readingProgressPercent }}%</p>
      </div>
    </div>

    <div v-if="showToc" class="reader-toc-layer fixed inset-0 z-[72]" @click.self="showToc = false">
      <div class="reader-toc-panel h-full w-[86vw] max-w-sm overflow-y-auto p-5 shadow-fruit">
        <div class="mb-5">
          <div>
            <div class="text-xs font-black text-white/70">目录</div>
            <h2 class="mt-1 text-lg font-black text-white">{{ book.title }}</h2>
          </div>
        </div>
        <button
          v-for="item in chapters"
          :key="item.id"
          class="reader-toc-item mb-2 flex w-full items-center justify-between rounded-xl px-3 py-3 text-left transition"
          :class="item.chapterNumber === currentChapter ? 'is-active text-white' : 'text-white/78'"
          @click="goToChapter(item.chapterNumber)"
        >
          <span class="truncate text-sm font-bold">{{ item.title || `第${item.chapterNumber}章` }}</span>
          <span class="ml-3 shrink-0 text-xs text-white/56">{{ tocPageLabel(item) }}</span>
        </button>
      </div>
    </div>

    <div v-if="showCreatorAchievement" class="fixed inset-0 z-[70] grid place-items-center bg-ink-900/40 px-4 backdrop-blur-sm" @click.self="showCreatorAchievement = false">
      <div class="w-full max-w-md rounded-2xl border border-jackfruit-100 bg-white p-6 text-center shadow-fruit">
        <div class="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-jackfruit-100 text-3xl">🏆</div>
        <h2 class="text-xl font-black text-ink-900">你解锁了创作者身份</h2>
        <p class="mt-2 text-sm leading-6 text-ink-500">
          {{ creatorUnlockText }}要把它整理成一本新作品吗？
        </p>
        <div class="mt-5 grid gap-2">
          <button class="rounded-xl bg-jackfruit-500 px-4 py-3 text-sm font-black text-ink-900 shadow-fruit" @click="openPublishDialog">
            以创作者身份保存并上架
          </button>
          <button class="rounded-xl border border-jackfruit-200 bg-white px-4 py-3 text-sm font-black text-ink-600" @click="showCreatorAchievement = false">
            继续读，晚点再说
          </button>
        </div>
      </div>
    </div>

    <div v-if="showExistingShelfDialog && existingShelfBook" class="fixed inset-0 z-[75] grid place-items-center bg-ink-900/40 px-4 backdrop-blur-sm" @click.self="showExistingShelfDialog = false">
      <div class="w-full max-w-sm rounded-2xl border border-jackfruit-100 bg-white p-5 text-center shadow-fruit">
        <h2 class="text-lg font-black text-ink-900">书架中已有此书</h2>
        <p class="mt-2 text-sm leading-6 text-ink-500">
          你的书架里已经有《{{ existingShelfBook.title }}》。可以回到书架里的版本继续读，也可以把公共书当成新的阅读重新开始。
        </p>
        <div class="mt-5 grid gap-2">
          <button class="rounded-xl bg-jackfruit-500 px-4 py-3 text-sm font-black text-ink-900 shadow-fruit" @click="continueExistingShelfBook">
            去书架继续阅读
          </button>
          <button class="rounded-xl border border-jackfruit-200 bg-white px-4 py-3 text-sm font-black text-ink-700" @click="startFreshPublicReading">
            开启新阅读
          </button>
        </div>
      </div>
    </div>

    <div v-if="showSaveForkDialog" class="fixed inset-0 z-[78] grid place-items-center bg-ink-900/40 px-4 backdrop-blur-sm">
      <div class="w-full max-w-sm rounded-2xl border border-jackfruit-100 bg-white p-5 shadow-fruit">
        <h2 class="text-lg font-black text-ink-900">另存为新阅读</h2>
        <p class="mt-2 text-sm leading-6 text-ink-500">这次阅读会作为一本新的私人书加入书架。</p>
        <input
          v-model="saveForkTitle"
          class="mt-4 w-full rounded-xl border border-jackfruit-200 px-3 py-3 text-sm font-bold text-ink-900 outline-none focus:border-jackfruit-400"
          maxlength="80"
        />
        <div class="mt-5 grid grid-cols-2 gap-2">
          <button class="rounded-xl border border-jackfruit-200 bg-white px-4 py-3 text-sm font-black text-ink-600" @click="cancelSaveFork">
            不保存
          </button>
          <button class="rounded-xl bg-jackfruit-500 px-4 py-3 text-sm font-black text-ink-900 shadow-fruit" @click="confirmSaveFork">
            好
          </button>
        </div>
      </div>
    </div>

    <div v-if="showPublishDialog" class="fixed inset-0 z-[80] grid place-items-center overflow-y-auto bg-ink-900/45 px-4 py-8 backdrop-blur-sm" @click.self="showPublishDialog = false">
      <div class="w-full max-w-2xl rounded-2xl border border-jackfruit-100 bg-white p-5 shadow-fruit md:p-6">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="text-xl font-black text-ink-900">整理成你的新作品</h2>
            <p class="mt-1 text-sm text-ink-500">选一个书名和封面，上架后会成为公共书库里的新书。</p>
          </div>
          <button class="rounded-xl border border-ink-100 px-3 py-2 text-sm font-black text-ink-500" @click="showPublishDialog = false">关闭</button>
        </div>

        <div class="mt-5">
          <div class="mb-2 flex items-center justify-between gap-3">
            <span class="text-sm font-black text-ink-900">书名</span>
            <button
              class="rounded-lg border border-jackfruit-200 px-3 py-1.5 text-xs font-black text-ink-600 hover:bg-jackfruit-50 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isPreparingPublish"
              @click="generatePublishTitleOptions"
            >
              重新生成书名
            </button>
          </div>
          <div class="mb-3 flex flex-wrap gap-2">
            <button
              v-for="title in titleOptions"
              :key="title"
              class="rounded-xl border px-3 py-2 text-sm font-black transition"
              :class="publishingTitle === title ? 'border-jackfruit-400 bg-jackfruit-100 text-ink-900' : 'border-jackfruit-100 bg-white text-ink-600'"
              @click="publishingTitle = title"
            >
              {{ title }}
            </button>
          </div>
          <input
            v-model="publishingTitle"
            maxlength="80"
            class="w-full rounded-xl border border-jackfruit-200 bg-white px-4 py-3 text-sm font-bold text-ink-900 outline-none focus:border-jackfruit-400"
            placeholder="也可以自己写一个书名"
          />
        </div>

        <div class="mt-5">
          <div class="mb-2 flex items-center justify-between">
            <span class="text-sm font-black text-ink-900">封面</span>
            <button
              class="rounded-lg border border-jackfruit-200 px-3 py-1.5 text-xs font-black text-ink-600 hover:bg-jackfruit-50 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isPreparingPublish"
              @click="generatePublishCoverOptions"
            >
              {{ isPreparingPublish ? '正在生成...' : '重新生成封面' }}
            </button>
          </div>
          <div class="grid grid-cols-3 gap-3">
            <button
              v-for="cover in coverOptions"
              :key="cover"
              class="overflow-hidden rounded-xl border-2 bg-jackfruit-50 transition"
              :class="selectedCoverUrl === cover ? 'border-jackfruit-400 shadow-fruit' : 'border-transparent'"
              @click="selectedCoverUrl = cover"
            >
              <img :src="cover" :alt="publishingTitle" class="aspect-[3/4] w-full object-cover" />
            </button>
          </div>
        </div>

        <div v-if="publishError" class="mt-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
          {{ publishError }}
        </div>

        <div class="mt-6 flex justify-end gap-3">
          <button class="rounded-xl border border-jackfruit-200 bg-white px-5 py-3 text-sm font-black text-ink-600" @click="showPublishDialog = false">
            先不上架
          </button>
          <button
            :disabled="isPublishing || !publishingTitle.trim() || !selectedCoverUrl"
            class="rounded-xl bg-jackfruit-500 px-5 py-3 text-sm font-black text-ink-900 shadow-fruit transition hover:bg-jackfruit-600 disabled:cursor-not-allowed disabled:opacity-40"
            @click="publishBook"
          >
            {{ isPublishing ? '提交中...' : '提交审核' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, onBeforeUnmount, nextTick } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { readingApi } from '@/api'
import { useAuthStore } from '@/stores/auth'
import type { Chapter } from '@/types/user'
import type { ReadingPageRestoreMode } from '@/types/reading'
import { useReadingAiBranch } from '@/composables/reading/useReadingAiBranch'
import { useChapterUnlock } from '@/composables/reading/useChapterUnlock'
import { useReadingData } from '@/composables/reading/useReadingData'
import { useReadingPublish } from '@/composables/reading/useReadingPublish'
import { requestMobileBrowserChromeCollapse } from '@/utils/browserChrome'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const {
  isLoading,
  error,
  book,
  chapters,
  chapter,
  currentChapter,
  isInBookshelf,
  isAddingToBookshelf,
  existingShelfBook,
  showExistingShelfDialog,
  isFreshPublicReading,
  isShelfEntryRoute,
  defaultForkTitle,
  isPublicUnshelvedReading,
  shouldPersistReadingProgress,
  setReadingDataHooks,
  loadBookData,
  loadChapterContent,
  findChapterIdByNumber,
  addToBookshelf,
  saveCurrentReadingToBookshelf
} = useReadingData({
  route,
  router,
  authStore
})

const hasAskedSaveOnLeave = ref(false)
const hasShownCreatorAchievement = ref(false)
const showCreatorAchievement = ref(false)
const showToc = ref(false)
const showReaderSettings = ref(false)
const showBrightnessPanel = ref(false)
const showProgressPanel = ref(false)
const showSaveForkDialog = ref(false)
const showReaderControls = ref(false)
const readerFontSize = ref(18)
const readerFont = ref<'system' | 'serif' | 'kai'>('system')
const readerPaper = ref<'cream' | 'white' | 'green' | 'dark' | 'pink'>('cream')
const readingMode = ref<'page' | 'scroll'>('page')
const readerBrightness = ref(100)
const currentPageIndex = ref(0)
const saveForkTitle = ref('')
const pendingLeaveNext = ref<null | ((valid?: boolean) => void)>(null)
const touchStartX = ref(0)
const touchStartY = ref(0)
const pageRestoreMode = ref<ReadingPageRestoreMode>('saved')

const readerFonts = [
  { value: 'system' as const, label: '系统' },
  { value: 'serif' as const, label: '宋体' },
  { value: 'kai' as const, label: '楷体' }
]

const paperOptions = [
  { value: 'cream' as const, label: '暖纸', preview: '#fffaf0' },
  { value: 'white' as const, label: '白纸', preview: '#fffdf8' },
  { value: 'green' as const, label: '护眼', preview: '#f2f6e8' },
  { value: 'dark' as const, label: '夜间', preview: '#24211d' },
  { value: 'pink' as const, label: '粉纸', preview: '#fff2f4' }
]

const readerIcons = {
  menu: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 7h14M5 12h14M5 17h14"/></svg>',
  bookmark: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4h10v16l-5-3-5 3z"/></svg>',
  brightness: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v3M12 19v3M4.9 4.9 7 7M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1 7 17M17 7l2.1-2.1"/></svg>',
  progress: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 18h14M5 6h14"/><circle cx="9" cy="18" r="2"/><circle cx="16" cy="6" r="2"/></svg>',
  pageMode: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5 3 12l5 7M16 5l5 7-5 7"/><path d="M11 5v14M13 5v14"/></svg>',
  scrollMode: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4h10v16H7z"/><path d="M10 8h4M10 12h4M10 16h4"/></svg>'
}

// 计算属性
const chapterTitle = computed(() => {
  return chapter.value.title || `第${currentChapter.value}章`
})

const unlockPack = computed(() => {
  const fallbackStart = currentChapter.value <= 3
    ? 1
    : 4 + Math.floor((currentChapter.value - 4) / 3) * 3
  const pack = (chapter.value as any).unlockPack || (chapter.value as any).pack
  return {
    start: pack?.start || fallbackStart,
    end: pack?.end || (currentChapter.value <= 3 ? 3 : fallbackStart + 2),
    price: pack?.price || chapter.value.price || 5
  }
})

const honeyBeanBalance = computed(() => Math.floor(authStore.user?.balance || 0))

const readingProgressPercent = computed(() => {
  if (!book.value.totalChapters) return 0
  const chapterBase = Math.max(0, currentChapter.value - 1)
  const pageRatio = pageCount.value ? (currentPageIndex.value + 1) / pageCount.value : 1
  return Math.min(100, Math.max(0, Math.round(((chapterBase + pageRatio) / book.value.totalChapters) * 100)))
})

const globalPageCount = computed(() => {
  return chapters.value.reduce((sum, item) => {
    if (item.chapterNumber === currentChapter.value) return sum + pageCount.value
    return sum + estimateChapterPageCount(item)
  }, 0)
})

const globalPageIndex = computed(() => {
  const previousPages = chapters.value
    .filter(item => item.chapterNumber < currentChapter.value)
    .reduce((sum, item) => sum + estimateChapterPageCount(item), 0)
  return Math.min(globalPageCount.value, previousPages + currentPageIndex.value + 1)
})

const readerStyle = computed(() => ({
  fontSize: `${readerFontSize.value}px`,
  lineHeight: readerFontSize.value >= 20 ? '1.72' : '1.66',
  fontFamily: readerFont.value === 'system'
    ? '-apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", "Microsoft YaHei", system-ui, sans-serif'
    : readerFont.value === 'kai'
      ? '"Kaiti SC", "Kaiti TC", "STKaiti", "KaiTi", "DFKai-SB", "BiauKai", serif'
      : '"Noto Serif SC", "Songti SC", "STSong", serif'
}))

const readerPaperClass = computed(() => {
  const map = {
    cream: 'bg-[#fffaf0]',
    white: 'bg-[#fffdf8]',
    green: 'bg-[#f2f6e8]',
    dark: 'reader-paper-dark',
    pink: 'bg-[#fff2f4]'
  }
  return map[readerPaper.value]
})

const readingModeClass = computed(() => {
  return readingMode.value === 'page' ? 'reader-page-mode' : 'reader-scroll-mode'
})

const readerBrightnessStyle = computed(() => {
  return { filter: `brightness(${readerBrightness.value}%)` }
})

const pageSize = computed(() => {
  if (typeof window !== 'undefined' && window.innerWidth < 768) {
    return readerFontSize.value >= 20 ? 310 : 390
  }
  return readerFontSize.value >= 20 ? 620 : 740
})

const normalizeTitleForCompare = (text: string) => {
  return text
    .replace(/^#+\s*/, '')
    .replace(/^第[一二三四五六七八九十百千万\d]+章[：:\s-]*/, '')
    .replace(/\s+/g, '')
    .trim()
}

const cleanChapterBody = (content: string, title: string) => {
  const cleanedContent = content
    .replace(/\.{3}（内容已截断，完整内容请阅读后续章节）/g, '')
    .replace(/…?（内容已截断，完整内容请阅读后续章节）/g, '')
    .trim()
  const lines = cleanedContent.split('\n')
  const titleKey = normalizeTitleForCompare(title)

  while (lines.length) {
    const first = lines[0].trim()
    const firstKey = normalizeTitleForCompare(first)
    if (!first || firstKey === titleKey || /^#+\s*第[一二三四五六七八九十百千万\d]+章/.test(first)) {
      lines.shift()
      continue
    }
    break
  }

  return lines.join('\n').trim()
}

const paginateContent = (title: string, body: string) => {
  const content = [title, body].filter(Boolean).join('\n\n')
  if (!content.trim() || chapter.value.hasAccess === false) return ['']
  const paragraphs = content.split(/\n{2,}/).map(item => item.trim()).filter(Boolean)
  const pages: string[] = []
  let current = ''

  for (const paragraph of paragraphs) {
    const next = current ? `${current}\n\n${paragraph}` : paragraph
    if (next.length > pageSize.value && current) {
      pages.push(current)
      current = paragraph
    } else {
      current = next
    }
  }

  if (current) pages.push(current)
  return pages.length ? pages : [content]
}

function estimateChapterPageCount(item: Chapter) {
  if (item.chapterNumber === currentChapter.value) return contentPageCount.value
  const titleLength = (item.title || `第${item.chapterNumber}章`).length
  return Math.max(1, Math.ceil(((item.wordCount || 800) + titleLength) / pageSize.value))
}

const chapterPages = computed(() => {
  return paginateContent(chapterTitle.value, cleanChapterBody(chapter.value.content || '', chapterTitle.value))
})

const contentPageCount = computed(() => chapterPages.value.length)
const isLatestChapter = computed(() => currentChapter.value >= book.value.totalChapters)
const hasEndingPage = computed(() => chapter.value.hasAccess !== false && isEndOfChapter.value && isLatestChapter.value)
const pageCount = computed(() => {
  if (readingMode.value === 'scroll') return contentPageCount.value
  return contentPageCount.value + (hasEndingPage.value ? 1 : 0)
})

const displayedContent = computed(() => {
  if (readingMode.value === 'scroll') {
    return chapterPages.value.join('\n\n')
  }
  return chapterPages.value[Math.min(currentPageIndex.value, contentPageCount.value - 1)] || ''
})

const displayedParagraphs = computed(() => {
  return displayedContent.value
    .split(/\n{2,}/)
    .map(text => text.trim())
    .filter(Boolean)
    .map((text, index) => ({
      text,
      isTitle: index === 0 && text === chapterTitle.value
    }))
})

const showChapterText = computed(() => {
  return readingMode.value === 'scroll' || currentPageIndex.value < contentPageCount.value
})

const showReaderWatermark = computed(() => {
  return chapter.value.hasAccess !== false && showChapterText.value && Boolean(book.value.title)
})

const showDesktopPageControls = computed(() => {
  return readingMode.value === 'page' && showChapterText.value
})

const showChapterEnding = computed(() => {
  return chapter.value.hasAccess !== false
    && isLatestChapter.value
    && (readingMode.value === 'scroll' || currentPageIndex.value >= contentPageCount.value)
})

const showScrollNextChapterButton = computed(() => {
  return readingMode.value === 'scroll'
    && chapter.value.hasAccess !== false
    && currentChapter.value < book.value.totalChapters
})

const tocPageLabel = (item: Chapter) => {
  const previousPages = chapters.value
    .filter(chapterItem => chapterItem.chapterNumber < item.chapterNumber)
    .reduce((sum, chapterItem) => sum + estimateChapterPageCount(chapterItem), 0)
  return `第 ${previousPages + 1} 页`
}

const canGoPreviousPage = computed(() => currentPageIndex.value > 0 || currentChapter.value > 1)
const canGoNextPage = computed(() => currentPageIndex.value < pageCount.value - 1 || currentChapter.value < book.value.totalChapters)

const creatorCustomPercent = computed(() => {
  if (!book.value.totalChoiceCount) return 0
  return Math.round(((book.value.customChoiceCount || 0) / book.value.totalChoiceCount) * 100)
})

const isPersonalOriginalCreation = computed(() => {
  return book.value.visibility === 'private' && book.value.originType === 'original' && !book.value.sourceBookId
})

const userCreatedChapterCount = computed(() => {
  return chapters.value.filter(item => item.contributionType !== 'inherited').length
})

const canPublishAsCreator = computed(() => {
  if (book.value.visibility !== 'private') return false
  if (isPersonalOriginalCreation.value) {
    return book.value.totalChapters >= 5
  }

  return userCreatedChapterCount.value >= 20
    && (book.value.totalChoiceCount || 0) >= 20
    && ((book.value.customChoiceCount || 0) / (book.value.totalChoiceCount || 1)) >= 0.5
})

const creatorUnlockText = computed(() => {
  if (isPersonalOriginalCreation.value) {
    return `这本原创书已经写到第 ${book.value.totalChapters} 章，可以整理书名和封面后申请上架。`
  }

  return `你已新增 ${userCreatedChapterCount.value}/20 章，自定义方向占比 ${creatorCustomPercent.value}%。新增满20章且自定义方向至少50%后，可以整理书名和封面后上架。`
})

const isPublicCreatorBook = computed(() => {
  return book.value.visibility === 'public'
    && book.value.status === 'published'
    && Boolean(book.value.ownerUserId)
    && book.value.originType !== 'seed'
})

// 是否为章节末尾（显示分支的判断条件：不是在加载中，没有错误，已有正常内容）
const isEndOfChapter = computed(() => {
  return !isLoading.value && !error.value && chapter.value.content && chapter.value.content.length > 50
})

const genreLabel = computed(() => {
  return book.value.category === 'male' ? '武侠'
    : book.value.category === 'female' ? '言情'
      : book.value.category === 'cyberpunk' ? '赛博朋克'
      : '都市'
})

const chapterNumberLabel = (chapterNumber: number) => {
  const digits = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九']
  if (chapterNumber <= 10) return chapterNumber === 10 ? '十' : digits[chapterNumber]
  if (chapterNumber < 20) return `十${digits[chapterNumber % 10]}`
  if (chapterNumber < 100) {
    const ten = Math.floor(chapterNumber / 10)
    const one = chapterNumber % 10
    return `${digits[ten]}十${one ? digits[one] : ''}`
  }
  return String(chapterNumber)
}

const formatGeneratedChapterTitle = (chapterNumber: number, title: string) => {
  const cleanTitle = title.trim().replace(/^第[一二三四五六七八九十百千万\d]+章[：:\s-]*/, '')
  return `第${chapterNumberLabel(chapterNumber)}章：${cleanTitle || '新的篇章'}`
}

const updateCreatorAchievement = () => {
  if (canPublishAsCreator.value && !hasShownCreatorAchievement.value) {
    hasShownCreatorAchievement.value = true
    showCreatorAchievement.value = true
  }
}

const pageStorageKey = (bookId = book.value.id, chapterNumber = currentChapter.value) => {
  return `pineapple-reader-page:${bookId}:${chapterNumber}`
}

const savePagePosition = () => {
  if (typeof window === 'undefined' || !book.value.id || readingMode.value !== 'page') return
  localStorage.setItem(pageStorageKey(), String(currentPageIndex.value))
}

const restorePagePosition = () => {
  if (pageRestoreMode.value === 'first') {
    currentPageIndex.value = 0
  } else if (pageRestoreMode.value === 'last') {
    currentPageIndex.value = Math.max(0, pageCount.value - 1)
  } else if (pageRestoreMode.value === 'lastContent') {
    currentPageIndex.value = Math.max(0, contentPageCount.value - 1)
  } else if (typeof window !== 'undefined') {
    const saved = Number(localStorage.getItem(pageStorageKey()))
    currentPageIndex.value = Number.isFinite(saved)
      ? Math.min(Math.max(0, saved), Math.max(0, pageCount.value - 1))
      : 0
  } else {
    currentPageIndex.value = 0
  }

  pageRestoreMode.value = 'saved'
}

const { isUnlocking, unlockChapter } = useChapterUnlock({
  authStore,
  router,
  currentChapter,
  findChapterIdByNumber,
  loadChapterContent
})

const previousChapter = () => {
  if (currentChapter.value > 1) {
    navigateToChapter(currentChapter.value - 1, 'lastContent')
  }
}

const nextChapter = () => {
  if (currentChapter.value < book.value.totalChapters) {
    navigateToChapter(currentChapter.value + 1, 'first')
  }
}

const retryLoading = () => {
  loadBookData()
}

const continueExistingShelfBook = () => {
  if (!existingShelfBook.value) return
  showExistingShelfDialog.value = false
  router.replace(`/reading/${existingShelfBook.value.id}/${Math.max(1, existingShelfBook.value.lastChapterNumber || 1)}?from=shelf`)
}

const startFreshPublicReading = () => {
  showExistingShelfDialog.value = false
  router.replace(`/reading/${book.value.id}/${currentChapter.value}?new=1`)
}

const ensureFreshReadingName = () => {
  if (book.value.visibility !== 'public' || !isFreshPublicReading.value) return ''
  const input = window.prompt('这条新阅读保存到书架时，想叫什么名字？', defaultForkTitle.value)
  if (input === null) return null
  return input.trim() || defaultForkTitle.value
}

const {
  aiStatus,
  isCheckingAI,
  branchOptions,
  selectedBranchIndex,
  isGeneratingBranch,
  isConfirmingBranch,
  customBranchText,
  showRewriteComposer,
  isAIAvailable,
  aiStatusText,
  aiStatusColor,
  showBranchComposer,
  showPublicCreatorContinue,
  shouldShowBranchStartButton,
  chapterEndTitle,
  chapterEndDescription,
  branchTitle,
  resetBranchState,
  openRewriteComposer,
  checkAIServiceStatus,
  maybeGenerateBranchOptions,
  generateBranchOptions,
  confirmBranch,
  confirmCustomBranch
} = useReadingAiBranch({
  book,
  chapter,
  chapters,
  currentChapter,
  route,
  router,
  genreLabel,
  isPublicCreatorBook,
  canPublishAsCreator,
  isEndOfChapter,
  showChapterEnding,
  ensureFreshReadingName,
  formatGeneratedChapterTitle,
  loadChapterContent
})

const {
  showPublishDialog,
  titleOptions,
  publishingTitle,
  coverOptions,
  selectedCoverUrl,
  isPreparingPublish,
  isPublishing,
  publishError,
  generatePublishTitleOptions,
  generatePublishCoverOptions,
  openPublishDialog,
  publishBook
} = useReadingPublish({
  book,
  chapter,
  router,
  genreLabel,
  isPersonalOriginalCreation,
  showCreatorAchievement
})

setReadingDataHooks({
  afterChapterLoaded: () => {
    restorePagePosition()
    maybeGenerateBranchOptions()
  },
  afterBookLoaded: updateCreatorAchievement,
  checkAIServiceStatus
})

const resetPage = () => {
  currentPageIndex.value = 0
}

const increaseFontSize = () => {
  readerFontSize.value = Math.min(24, readerFontSize.value + 1)
  currentPageIndex.value = Math.min(currentPageIndex.value, pageCount.value - 1)
}

const decreaseFontSize = () => {
  readerFontSize.value = Math.max(15, readerFontSize.value - 1)
  currentPageIndex.value = Math.min(currentPageIndex.value, pageCount.value - 1)
}

const toggleBookmark = () => {
  alert('已为当前页添加书签')
}

const toggleReadingMode = async () => {
  if (readingMode.value === 'page') {
    const ratio = contentPageCount.value > 1 ? currentPageIndex.value / (contentPageCount.value - 1) : 0
    readingMode.value = 'scroll'
    await nextTick()
    const scrollable = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
    window.scrollTo({ top: scrollable * ratio, behavior: 'instant' as ScrollBehavior })
  } else {
    const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight)
    const ratio = Math.min(1, Math.max(0, window.scrollY / scrollable))
    currentPageIndex.value = Math.min(contentPageCount.value - 1, Math.round(ratio * Math.max(0, contentPageCount.value - 1)))
    readingMode.value = 'page'
    await nextTick()
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
  }
}

const previousPage = () => {
  requestMobileBrowserChromeCollapse()
  if (currentPageIndex.value > 0) {
    currentPageIndex.value--
    return
  }
  if (currentChapter.value > 1) {
    navigateToChapter(currentChapter.value - 1, 'lastContent')
  }
}

const nextPage = () => {
  requestMobileBrowserChromeCollapse()
  if (currentPageIndex.value < pageCount.value - 1) {
    currentPageIndex.value++
    if (showChapterEnding.value) maybeGenerateBranchOptions()
    return
  }
  if (currentChapter.value < book.value.totalChapters) {
    navigateToChapter(currentChapter.value + 1, 'first')
  }
}

const goToChapter = (chapterNumber: number) => {
  showToc.value = false
  navigateToChapter(chapterNumber, 'first')
}

const navigateToChapter = (chapterNumber: number, restoreMode: 'saved' | 'first' | 'last' | 'lastContent' = 'saved') => {
  pageRestoreMode.value = restoreMode
  if (chapterNumber === currentChapter.value) {
    restorePagePosition()
    return
  }
  router.push(`/reading/${book.value.id}/${chapterNumber}${isShelfEntryRoute.value ? '?from=shelf' : ''}`)
}

const goBackToBookshelf = () => {
  router.push('/bookshelf')
}

const handleReaderTap = (event: MouseEvent) => {
  requestMobileBrowserChromeCollapse()
  const target = event.target as HTMLElement
  if (target.closest('button, textarea, input, a')) return

  if (readingMode.value === 'page') {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
    const xRatio = (event.clientX - rect.left) / rect.width
    const yRatio = (event.clientY - rect.top) / rect.height
    const isControlsHotspot = xRatio > 0.42 && xRatio < 0.58 && yRatio > 0.32 && yRatio < 0.72
    if (!isControlsHotspot) {
      if (xRatio < 0.5) previousPage()
      else nextPage()
      return
    }
  }

  showReaderControls.value = !showReaderControls.value
}

const handleTouchStart = (event: TouchEvent) => {
  const touch = event.changedTouches[0]
  touchStartX.value = touch.clientX
  touchStartY.value = touch.clientY
}

const handleTouchEnd = (event: TouchEvent) => {
  requestMobileBrowserChromeCollapse()
  if (readingMode.value !== 'page') return
  const touch = event.changedTouches[0]
  const deltaX = touch.clientX - touchStartX.value
  const deltaY = touch.clientY - touchStartY.value
  if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return
  if (deltaX < 0) {
    nextPage()
  } else {
    previousPage()
  }
}

const handleReaderKeydown = (event: KeyboardEvent) => {
  const target = event.target as HTMLElement | null
  if (target?.closest('input, textarea, select, [contenteditable="true"]')) return
  if (readingMode.value !== 'page') return

  if (event.key === 'ArrowLeft') {
    event.preventDefault()
    previousPage()
  } else if (event.key === 'ArrowRight') {
    event.preventDefault()
    nextPage()
  }
}

const cancelSaveFork = () => {
  showSaveForkDialog.value = false
  const next = pendingLeaveNext.value
  pendingLeaveNext.value = null
  next?.()
}

const confirmSaveFork = async () => {
  try {
    await saveCurrentReadingToBookshelf(saveForkTitle.value)
  } catch (err: any) {
    console.error('保存到书架失败:', err)
    alert(err.message || '保存失败，请稍后重试')
    return
  }

  showSaveForkDialog.value = false
  const next = pendingLeaveNext.value
  pendingLeaveNext.value = null
  next?.()
}

// 监听路由变化
watch(() => route.params, (newParams) => {
  const bookId = parseInt(newParams.bookId as string)
  const chapterId = parseInt(newParams.chapterId as string) || 1
  
  if (bookId !== book.value.id || chapterId !== currentChapter.value) {
    resetBranchState()
    loadBookData()
  }
})

watch(currentPageIndex, () => {
  savePagePosition()
})

// 生命周期
onMounted(() => {
  document.body.classList.add('reading-mode')
  window.addEventListener('keydown', handleReaderKeydown)
  loadBookData()
})

// 离开页面时保存阅读进度
onBeforeRouteLeave(async (_to, _from, next) => {
  if (!isPublicUnshelvedReading.value || hasAskedSaveOnLeave.value) {
    next()
    return
  }

  hasAskedSaveOnLeave.value = true

  if (isFreshPublicReading.value) {
    saveForkTitle.value = defaultForkTitle.value
    pendingLeaveNext.value = next
    showSaveForkDialog.value = true
    return
  }

  const shouldSave = window.confirm('是否保存本书到书架？否则进度将丢失')

  if (shouldSave) {
    try {
      await saveCurrentReadingToBookshelf(saveForkTitle.value)
    } catch (err: any) {
      console.error('保存到书架失败:', err)
      alert(err.message || '保存失败，请稍后重试')
    }
  }

  next()
})

onBeforeUnmount(async () => {
  document.body.classList.remove('reading-mode')
  window.removeEventListener('keydown', handleReaderKeydown)
  if (shouldPersistReadingProgress.value && book.value.id && currentChapter.value > 0) {
    try {
      const chapterId = findChapterIdByNumber(currentChapter.value)
      if (chapterId) {
        await readingApi.updateProgress({
          bookId: book.value.id,
          chapterId,
          progress: (currentChapter.value / book.value.totalChapters) * 100
        })
      }
    } catch (err) {
      console.error('保存阅读进度失败:', err)
    }
  }
})
</script>

<style scoped>
:global(body.reading-mode) {
  padding-bottom: 0 !important;
  background: #fffaf0;
}

.reader-immersive {
  background: #fffaf0;
}

.reader-shell {
  min-height: 100vh;
}

.reader-canvas {
  min-height: 100vh;
  width: 100%;
}

.reader-back-btn {
  position: fixed;
  left: max(1rem, env(safe-area-inset-left));
  top: max(0.85rem, env(safe-area-inset-top));
  z-index: 80;
  display: grid;
  height: 2.35rem;
  width: 2.35rem;
  place-items: center;
  border-radius: 999px;
  background: rgb(255 255 255 / 0.76);
  color: #3c3328;
  font-size: 1.8rem;
  line-height: 1;
  box-shadow: 0 10px 30px rgb(92 58 18 / 0.08);
}

.reader-tool-btn {
  border: 1px solid rgb(255 227 159);
  border-radius: 999px;
  background: rgb(255 255 255 / 0.88);
  padding: 0.55rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 900;
  color: #5f5648;
  transition: all 0.2s ease;
}

.reader-tool-btn:hover:not(:disabled) {
  background: #fff7d7;
  color: #251f19;
}

.reader-tool-btn:disabled {
  opacity: 0.38;
}

.reader-icon-btn {
  display: inline-grid;
  height: 2.25rem;
  width: 2.25rem;
  place-items: center;
  border: 1px solid rgb(255 227 159);
  border-radius: 999px;
  background: rgb(255 255 255 / 0.9);
  font-size: 0.95rem;
  font-weight: 900;
  color: #4d4438;
  transition: all 0.18s ease;
}

.reader-icon-btn:hover {
  background: #fff7d7;
  color: #251f19;
}

.reader-mini-btn {
  border: 1px solid rgb(255 227 159);
  border-radius: 999px;
  background: rgb(255 255 255 / 0.82);
  padding: 0.35rem 0.75rem;
  font-weight: 900;
  color: #6f6250;
}

.reader-page {
  min-height: 100vh;
  padding-bottom: 6.8rem;
}

.reader-page-mode {
  height: 100dvh;
  overflow: hidden;
}

.reader-scroll-mode {
  min-height: 100vh;
  overflow: visible;
}

.reader-bottom-bar {
  position: fixed;
  inset-inline: 0;
  bottom: 0;
  z-index: 70;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.15rem;
  margin-inline: auto;
  width: min(26rem, calc(100% - 2rem));
  border: 0;
  border-radius: 999px;
  background: rgb(44 42 39 / 0.32);
  padding: 0.25rem 0.45rem max(0.25rem, env(safe-area-inset-bottom));
  backdrop-filter: blur(10px) saturate(1.04);
  -webkit-backdrop-filter: blur(10px) saturate(1.04);
  box-shadow: 0 14px 36px rgb(55 42 24 / 0.08);
  opacity: 0;
  pointer-events: none;
  transform: translateY(110%);
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.reader-bottom-bar.is-visible {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(-1rem);
}

.reader-bottom-btn {
  display: grid;
  min-height: 2.05rem;
  place-items: center;
  background: transparent;
  color: rgb(255 255 255 / 0.94);
  font-size: 1rem;
  font-weight: 900;
}

.reader-bottom-btn :deep(svg),
.reader-bottom-btn svg {
  width: 1.35rem;
  height: 1.35rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.reader-letter-btn {
  color: rgb(255 255 255 / 0.94);
  font-size: 1.55rem;
  font-family: Georgia, serif;
}

.reader-scroll-next-btn {
  position: fixed;
  right: max(1.15rem, env(safe-area-inset-right));
  bottom: max(1.1rem, env(safe-area-inset-bottom));
  z-index: 28;
  border: 1px solid rgb(255 213 112 / 0.78);
  border-radius: 999px;
  background: rgb(255 250 240 / 0.9);
  padding: 0.55rem 0.9rem;
  color: #1f1b16;
  font-size: 0.85rem;
  font-weight: 900;
  box-shadow: 0 10px 28px rgb(92 58 18 / 0.12);
  backdrop-filter: blur(14px);
}

.reader-panel-layer {
  position: fixed;
  inset: 0;
  z-index: 76;
  display: grid;
  place-items: end center;
  padding: 0 1.25rem calc(4.8rem + env(safe-area-inset-bottom));
  background: transparent;
}

.reader-settings-panel,
.reader-compact-panel {
  width: min(100%, 28rem);
  border-radius: 1.5rem;
  border: 0;
  background: rgb(44 42 39 / 0.34);
  padding: 1.1rem;
  box-shadow: 0 18px 56px rgb(55 42 24 / 0.08);
  backdrop-filter: blur(10px) saturate(1.04);
  -webkit-backdrop-filter: blur(10px) saturate(1.04);
}

.reader-settings-panel .text-ink-500,
.reader-settings-panel .text-ink-600,
.reader-compact-panel .text-ink-500,
.reader-compact-panel .text-ink-600 {
  color: rgb(255 255 255 / 0.78);
}

.reader-compact-panel {
  width: min(100%, 24rem);
}

.reader-paper-row {
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding-top: 1rem;
}

.reader-paper-swatch {
  height: 2rem;
  width: 2rem;
  border: 2px solid rgb(111 98 80 / 0.16);
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px rgb(255 255 255 / 0.6);
}

.reader-paper-swatch.active {
  border-color: #b9851c;
  box-shadow: 0 0 0 3px rgb(185 133 28 / 0.14);
}

.reader-paper-dark .reader-paper-swatch.active {
  border-color: #f5d58b;
}

.reader-slider-row {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  border-radius: 999px;
  background: rgb(255 255 255 / 0.14);
  color: rgb(255 255 255 / 0.9);
  padding: 0.55rem 0.85rem;
}

.reader-segment {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.reader-segment-btn,
.reader-font-btn {
  min-height: 2.8rem;
  border-radius: 999px;
  background: rgb(255 255 255 / 0.13);
  color: rgb(255 255 255 / 0.88);
  font-weight: 900;
}

.reader-font-btn.active {
  background: rgb(255 255 255 / 0.28);
  color: #fff;
}

.reader-tap-zone {
  display: none;
}

.reader-desktop-page-btn {
  position: fixed;
  top: 50%;
  z-index: 30;
  display: none;
  transform: translateY(-50%);
  background: transparent;
  color: rgb(31 27 22 / 0.12);
  transition: color 0.18s ease, transform 0.18s ease, opacity 0.18s ease;
}

.reader-desktop-page-btn svg {
  width: clamp(3rem, 5vw, 5.8rem);
  height: clamp(3rem, 5vw, 5.8rem);
  fill: none;
  stroke: currentColor;
  stroke-width: 1.15;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.reader-desktop-page-btn:hover {
  color: rgb(31 27 22 / 0.42);
}

.reader-desktop-page-prev {
  left: max(0.45rem, env(safe-area-inset-left));
}

.reader-desktop-page-next {
  right: max(0.45rem, env(safe-area-inset-right));
}

.reader-watermark {
  position: fixed;
  top: max(1rem, env(safe-area-inset-top));
  right: max(1.2rem, env(safe-area-inset-right));
  z-index: 18;
  max-width: min(42vw, 16rem);
  color: rgb(31 27 22 / 0.28);
  line-height: 1.25;
  pointer-events: none;
  text-align: right;
}

.reader-watermark-brand {
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", "Microsoft YaHei", system-ui, sans-serif;
  font-size: 0.68rem;
  font-style: italic;
  font-weight: 700;
}

.reader-watermark-title {
  margin-top: 0.1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: "Noto Serif SC", "Songti SC", "STSong", serif;
  font-size: 0.82rem;
  font-weight: 800;
}

.reader-page-btn {
  border: 1px solid rgb(255 227 159);
  border-radius: 999px;
  background: rgb(255 255 255 / 0.76);
  padding: 0.55rem 1.15rem;
  font-size: 0.85rem;
  font-weight: 900;
  color: #5f5648;
}

.reader-page-btn:disabled {
  opacity: 0.36;
}

.reader-prose {
  position: relative;
  z-index: 12;
  letter-spacing: 0;
  min-height: 52vh;
}

.reader-text {
  word-break: break-word;
}

.reader-chapter-title {
  margin: 0 0 2.1rem;
  color: #1f1b16;
  font-size: 1.7em;
  font-weight: 900;
  line-height: 1.25;
}

.reader-paragraph {
  margin: 0 0 0.46em;
  line-height: inherit;
  text-align: left;
}

.reader-page-count {
  position: absolute;
  right: max(1.25rem, env(safe-area-inset-right));
  bottom: max(1.25rem, env(safe-area-inset-bottom));
  z-index: 20;
  color: rgb(95 86 72 / 0.58);
  font-size: 0.78rem;
  font-weight: 400;
  letter-spacing: 0.02em;
  pointer-events: none;
}

.reader-ending-panel {
  margin-top: 2.2rem;
  border-top: 2px dashed rgb(255 227 159);
  padding-top: 2.2rem;
}

.reader-ending-header {
  margin-bottom: 1.7rem;
}

.reader-branch-list {
  position: relative;
  z-index: 24;
}

.branch-choice {
  cursor: pointer;
  position: relative;
  z-index: 25;
  border-width: 1px;
  border-radius: 1rem;
  background: rgb(255 255 255 / 0.86);
  padding: 0.95rem 1.1rem;
  font-size: 0.95rem;
  font-weight: 900;
  line-height: 1.7;
  color: #2f281f;
  transition: all 0.18s ease;
}

.branch-choice:hover {
  transform: translateY(-1px);
  border-color: #ffc53d;
  background: #fffdf6;
}

.reader-toc-panel {
  background: rgb(255 255 255 / 0.16);
  backdrop-filter: blur(10px) saturate(1.04);
  -webkit-backdrop-filter: blur(10px) saturate(1.04);
  box-shadow: 0 18px 56px rgb(55 42 24 / 0.08);
}

.reader-toc-layer {
  background: rgb(40 35 28 / 0.08);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}

.reader-toc-item {
  background: transparent;
}

.reader-toc-item:hover,
.reader-toc-item.is-active {
  background: rgb(255 255 255 / 0.24);
}

.reader-paper-dark {
  background: #24211d;
}

.reader-paper-dark .reader-prose,
.reader-paper-dark .reader-prose h1,
.reader-paper-dark .text-ink-700,
.reader-paper-dark .text-ink-800,
.reader-paper-dark .text-ink-900,
.reader-paper-dark .reader-chapter-title,
.reader-paper-dark .reader-paragraph {
  color: #d8d2c6;
}

.reader-paper-dark .text-ink-500 {
  color: #aaa294;
}

.reader-paper-dark .reader-page-count {
  color: rgb(216 210 198 / 0.55);
}

.reader-paper-dark .reader-watermark {
  color: rgb(216 210 198 / 0.22);
}

.reader-paper-dark .reader-desktop-page-btn {
  color: rgb(216 210 198 / 0.14);
}

.reader-paper-dark .reader-desktop-page-btn:hover {
  color: rgb(216 210 198 / 0.42);
}

.reader-paper-dark .reader-scroll-next-btn {
  border-color: rgb(216 210 198 / 0.16);
  background: rgb(36 33 29 / 0.82);
  color: #d8d2c6;
}

.sticky {
  position: sticky;
  z-index: 10;
}

@media (max-width: 767px) {
  .reader-page {
    min-height: 100vh;
    border-radius: 0;
    border-left: 0;
    border-right: 0;
    box-shadow: none;
    padding: 4.25rem 1.25rem 4.2rem;
  }

  .reader-prose {
    font-size: 1.05em;
  }

  .reader-chapter-title {
    margin-top: 0.4rem;
    margin-bottom: 2.35rem;
    font-size: 1.55em;
  }

  .reader-paragraph {
    margin-bottom: 0.42em;
  }

  .reader-ending-panel {
    margin-top: 0;
    padding-top: 1.35rem;
  }

  .reader-ending-header {
    margin-bottom: 1.15rem;
  }

  .reader-tap-zone {
    position: absolute;
    top: 0;
    bottom: 12rem;
    z-index: 5;
    display: block;
    width: 42%;
    background: transparent;
  }

  .reader-tap-zone.right-0 {
    right: 0;
  }

  .reader-tap-zone.left-0 {
    left: 0;
  }

  .reader-page-count {
    right: max(1.25rem, env(safe-area-inset-right));
    bottom: max(1.8rem, calc(env(safe-area-inset-bottom) + 1rem));
  }
}

@media (min-width: 768px) {
  .reader-page-mode > .reader-prose {
    margin-inline: auto;
    width: min(100%, calc(100vw - 11rem));
  }

  .reader-desktop-page-btn {
    display: block;
  }
}
</style>
